import { useId } from 'react';
import { ACHIEVEMENT_DEFINITIONS, NAME_COLOR_REWARDS, type AchievementId } from '@the-wall/shared';

interface AchievementBadgeIconProps {
  achievementId: AchievementId;
  className?: string;
}

function InkSpent50Badge({ className = 'h-4 w-4' }: { className?: string }) {
  const bgId = useId();
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={bgId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.06" />
        </linearGradient>
      </defs>

      <circle cx="12" cy="12" r="10.5" fill={`url(#${bgId})`} />
      <circle cx="12" cy="12" r="10.5" fill="none" stroke="currentColor" strokeOpacity="0.45" />

      <path
        d="M7.6 15.8c2 .2 3.7 1.2 4.9 3.1 1.7-.3 2.8-1.2 3.4-2.8-1.3-.9-2.9-1.3-4.5-1.2-1.2.1-2.5.4-3.8.9Z"
        fill="currentColor"
        fillOpacity="0.35"
      />
      <path
        d="m10 12.8 4.9-4.9 1.3 1.3-4.9 4.9H10v-1.3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="8.1" cy="8.4" r="0.8" fill="currentColor" />
    </svg>
  );
}

function LoginStreakBadge({ className = 'h-4 w-4' }: { className?: string }) {
  const bgId = useId();
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={bgId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.06" />
        </linearGradient>
      </defs>

      <circle cx="12" cy="12" r="10.5" fill={`url(#${bgId})`} />
      <circle cx="12" cy="12" r="10.5" fill="none" stroke="currentColor" strokeOpacity="0.45" />
      {/* Calendar frame = "daily" */}
      <rect
        x="6.6"
        y="7.1"
        width="10.8"
        height="9.8"
        rx="1.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M9 6v2.2M15 6v2.2M6.8 10.1h10.4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />

      {/* Rising bars = streak growth */}
      <path
        d="M9.2 14.9v-1.5M12 14.9v-2.3M14.8 14.9v-3.1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TurfWarBadge({ className = 'h-4 w-4' }: { className?: string }) {
  const bgId = useId();
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={bgId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.06" />
        </linearGradient>
      </defs>

      <circle cx="12" cy="12" r="10.5" fill={`url(#${bgId})`} />
      <circle cx="12" cy="12" r="10.5" fill="none" stroke="currentColor" strokeOpacity="0.45" />
      <path
        d="M8.2 16.2 15.9 8.5l.9.9-7.7 7.7c-.4.4-1 .4-1.4 0-.4-.4-.4-1 0-1.4Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M15.8 16.2 8.1 8.5l-.9.9 7.7 7.7c.4.4 1 .4 1.4 0 .4-.4.4-1 0-1.4Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="m15.9 8.5 1.2-1.2M8.1 8.5 6.9 7.3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M10.9 13.1 13.1 10.9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChatBadge({ className = 'h-4 w-4' }: { className?: string }) {
  const bgId = useId();
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={bgId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.06" />
        </linearGradient>
      </defs>

      <circle cx="12" cy="12" r="10.5" fill={`url(#${bgId})`} />
      <circle cx="12" cy="12" r="10.5" fill="none" stroke="currentColor" strokeOpacity="0.45" />
      <path
        d="M8.1 7.7h7.8a1.9 1.9 0 0 1 1.9 1.9v5.3a1.9 1.9 0 0 1-1.9 1.9h-3.8l-2.1 2v-2H8.1a1.9 1.9 0 0 1-1.9-1.9V9.6a1.9 1.9 0 0 1 1.9-1.9Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M9.2 10.8h5.6M9.2 13.1h4.2"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ExpansionBadge({ className = 'h-4 w-4' }: { className?: string }) {
  const bgId = useId();
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <radialGradient id={bgId} cx="50%" cy="48%" r="58%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.42" />
          <stop offset="55%" stopColor="currentColor" stopOpacity="0.16" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.06" />
        </radialGradient>
      </defs>

      <circle cx="12" cy="12" r="10.5" fill={`url(#${bgId})`} />
      <circle cx="12" cy="12" r="10.5" fill="none" stroke="currentColor" strokeOpacity="0.45" />
      <path
        d="M12 5.7v3.1M12 15.2v3.1M5.7 12h3.1M15.2 12h3.1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="m7.8 7.8 2.1 2.1M14.1 14.1l2.1 2.1M16.2 7.8l-2.1 2.1M9.9 14.1l-2.1 2.1"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        opacity="0.75"
      />
      <rect
        x="9"
        y="9"
        width="6"
        height="6"
        rx="1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.45"
      />
    </svg>
  );
}

export function AchievementBadgeIcon({ achievementId, className }: AchievementBadgeIconProps) {
  const rewardDefinition = ACHIEVEMENT_DEFINITIONS.find(
    (definition) => definition.id === achievementId
  );
  if (
    rewardDefinition &&
    'rewardType' in rewardDefinition &&
    rewardDefinition.rewardType === 'name_color'
  ) {
    const reward = NAME_COLOR_REWARDS.find(
      (entry) => entry.id === rewardDefinition.rewardNameColorId
    );
    if (reward) {
      return (
        <div
          className={`${className ?? 'h-4 w-4'} rounded-full border border-white/20 shadow-[inset_0_1px_1px_rgba(255,255,255,0.18)]`}
          style={{ backgroundColor: reward.color }}
          aria-hidden="true"
        />
      );
    }
  }

  if (
    achievementId === 'ink_spent_50' ||
    achievementId === 'ink_spent_250' ||
    achievementId === 'ink_spent_800' ||
    achievementId === 'ink_spent_1500' ||
    achievementId === 'ink_spent_4000'
  ) {
    return <InkSpent50Badge className={className ?? 'h-4 w-4'} />;
  }
  if (
    achievementId === 'login_streak_3' ||
    achievementId === 'login_streak_7' ||
    achievementId === 'login_streak_15' ||
    achievementId === 'login_streak_30' ||
    achievementId === 'login_streak_60'
  ) {
    return <LoginStreakBadge className={className ?? 'h-4 w-4'} />;
  }
  if (
    achievementId === 'turf_war_15' ||
    achievementId === 'turf_war_50' ||
    achievementId === 'turf_war_100' ||
    achievementId === 'turf_war_200' ||
    achievementId === 'turf_war_500'
  ) {
    return <TurfWarBadge className={className ?? 'h-4 w-4'} />;
  }
  if (
    achievementId === 'chat_messages_20' ||
    achievementId === 'chat_messages_50' ||
    achievementId === 'chat_messages_100' ||
    achievementId === 'chat_messages_250' ||
    achievementId === 'chat_messages_800'
  ) {
    return <ChatBadge className={className ?? 'h-4 w-4'} />;
  }
  if (
    achievementId === 'expansion_witness' ||
    achievementId === 'canvas_detonator' ||
    achievementId === 'boom_maker' ||
    achievementId === 'big_bang_artist'
  ) {
    return <ExpansionBadge className={className ?? 'h-4 w-4'} />;
  }
  if (
    achievementId === 'daily_ink_zero_3' ||
    achievementId === 'daily_ink_zero_7' ||
    achievementId === 'daily_ink_zero_15' ||
    achievementId === 'daily_ink_zero_30' ||
    achievementId === 'daily_ink_zero_50'
  ) {
    return <InkSpent50Badge className={className ?? 'h-4 w-4'} />;
  }
  if (
    achievementId === 'archive_comments_5' ||
    achievementId === 'archive_comments_15' ||
    achievementId === 'archive_comments_25' ||
    achievementId === 'archive_comments_50' ||
    achievementId === 'archive_comments_100'
  ) {
    return <ChatBadge className={className ?? 'h-4 w-4'} />;
  }
  return null;
}
